<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import MemberCount from "$app/Pages/Signup/MemberCount.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Coupon from "$app/Pages/Signup/Coupon.svelte"
  import { appStore } from "$app/store.svelte"
  import { signupStore } from "$app/signupstore.svelte"
  import {
    xhr,
    toast,
    formatter,
    dateConverter,
    catchError,
    convertObjectToUrl
  } from "$app/utils"
  import { defer } from "underscore"
  import Card from "$app/Pages/Signup/Card.svelte"
  import SaveButton from "$app/Pages/Signup/SaveButton.svelte"
  import BillingIntervalButton from "$app/Pages/Signup/BillingIntervalButton.svelte"
  import ShowCard from "./ShowCard.svelte"
  import { Loader2 } from "lucide-svelte"

  let {
    oncancel,
    oncomplete
  }: {
    oncancel: () => void
    oncomplete: (subscription: SubscriptionType) => void
  } = $props()

  let card = $state<Card>(null)
  let saving: boolean = $state(false)
  let errors: Record<string, any> = $state({})
  let addCard = $state(false)
  let stripe_invoice: any = $state({})

  if (signupStore.subscription.billing_interval === "not_applicable") {
    signupStore.subscription.billing_interval = "year"
  }
  signupStore.selectedPlan = signupStore.products.find(
    (p) => p.id === "standard"
  )

  let loading = $state(true)
  async function calculateTotal(): Promise<void> {
    loading = true

    const data = {
      full_access_total: signupStore.signupCount("full_access"),
      team_member_total: signupStore.signupCount("team_member"),
      recognition_member_total: signupStore.signupCount("recognition_member"),
      subscription_type: "standard",
      billing_interval: signupStore.billingInterval,
      coupon: null
    }
    if (signupStore.coupon.valid) {
      data.coupon = signupStore.coupon.id
    }

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/admin/billing/calculate_update?${convertObjectToUrl(data)}`
      )

      stripe_invoice = resp.data.invoice
    } catch (e: any) {
      console.error(e)
      toast("There was an error calculating new totals", "error")
    } finally {
      loading = false
    }
  }

  async function getData(): Promise<any> {
    const auth_token_input = <HTMLInputElement>(
      document.getElementById("x-csrf-token")
    )
    const auth_token = auth_token_input?.value || ""

    let cardToken = ""
    if (addCard) {
      cardToken = await card.getStripeToken()
    }

    if (addCard && cardToken === "") {
      toast(
        "There was an error with the credit card verification. Please try again.",
        "error"
      )
      saving = false
      return
    }

    return {
      subscription: {
        stripe_token: cardToken,
        stripe_coupon: signupStore.coupon.id,
        billing_interval: signupStore.billingInterval,
        discount: signupStore.discount,
        subscription_type: "standard",
        name: signupStore.selectedPlan.name,
        full_access_total: signupStore.signupCount("full_access"),
        team_member_total: signupStore.signupCount("team_member"),
        recognition_member_total: signupStore.signupCount("recognition_member"),
        auth_token: auth_token
      }
    }
  }

  /**
   * Submit data
   */
  async function handleSubmit(): Promise<void> {
    saving = true
    const data = await getData()

    const [error, resp] = await catchError(
      xhr.post<{
        plan: PlanType
        subscription: SubscriptionType
      }>(`/plans/${appStore.plan.id}/admin/billing/update_standard`, {
        ...data
      })
    )

    saving = false
    if (error) {
      toast(
        "Error updating Standard Subscription. Please try again or contact support@leadwithpurpose.com",
        "error",
        { duration: 30000 }
      )
      return
    }

    toast(
      "Congratulations! You have successfully updated your Plan 🎉",
      "success"
    )
    appStore.plan = resp.data.plan
    signupStore.subscription = resp.data.subscription
    oncomplete(resp.data.subscription)
  }

  onMount(() => {
    signupStore.setSignupDataToSubscription()
    calculateTotal()
  })
</script>

<h3 class="text-xl py-4 font-semibold">Your Plan</h3>
<div class="border border-rounded border-gray-200 bg-white shadow">
  <div class="py-6 px-6">
    <div class="flex justify-between">
      <h3 class="text-xl text-black">Modify Standard Subscription</h3>
      <button
        class="text-base font-semibold py-0.5 px-2 border border-gray-200 hover:bg-gray-50 hover:text-gray-600 rounded-sm hover:shadow"
        onclick={oncancel}>Cancel</button
      >
    </div>
    <div>
      <div class="mt-4 text-base text-gray-700">
        {#if signupStore.stripe_data.subscription?.status === "trialing"}
          <p>
            You are currently in your trial period, which will end on
            <span class="font-bold"
              >{dateConverter(
                signupStore.stripe_data.subscription.trial_end
              )}</span
            >. Any changes you make today will affect your initial payment once
            your trial period is completed.
          </p>
        {:else}
          You may modify your subscription and update the number of members you
          have at each access level. You will be billed immediately for any
          member increases prorated until the end of your current billing cycle.
        {/if}
      </div>
    </div>
  </div>
</div>

<div
  transition:slide
  class="max-w-4xl mx-auto shadow-md border rounded-md py-4"
>
  <div class="px-2 md:px-4">
    <div class="grid grid-cols-4 gap-y-4 my-5 gap-x-4">
      {#each signupStore.member_roles as role (role.id)}
        <MemberCount
          role={role.id}
          min={role.min}
          max={role.max}
          onchange={(num) => {
            defer(() => {
              calculateTotal()
            }, 500)
          }}
        />
        <div>
          {#if signupStore.changesMemberCount(role.id) === 0}
            <span class="text-gray-500">No changes</span>
          {:else if signupStore.changesMemberCount(role.id) > 0}
            <span class="text-green-500 font-bold">
              +{signupStore.changesMemberCount(role.id)}
            </span>
          {:else}
            <span class="text-red-500 font-bold">
              {signupStore.changesMemberCount(role.id)}
            </span>
          {/if}
        </div>
      {/each}
    </div>

    <div class="relative mt-6 flex justify-center">
      <BillingIntervalButton />
    </div>

    <div class="px-12 py-4 max-w-2xl mx-auto shadow border rounded mt-4">
      <div class="grid grid-cols-3 gap-2">
        <div class="col-span-2 flex items-center justify-start gap-1">
          Subtotal
        </div>
        <div class="font-bold text-right leading-12">
          {#if loading}
            <Loader2 class="animate-spin" />
          {:else}
            {formatter.format(stripe_invoice.subtotal / 100)}
          {/if}
        </div>
      </div>

      <div class="grid grid-cols-3 gap-2">
        <div class="col-span-2 flex items-center gap-1">
          Discount
          {#if signupStore.stripe_data.subscription.discount?.coupon}
            {signupStore.stripe_data.subscription.discount.coupon.name}
          {:else}
            <Coupon
              onvalidating={(validating) => {
                saving = validating
                if (!validating) {
                  calculateTotal()
                }
              }}
            />
          {/if}
        </div>
        <div class="font-bold text-right leading-12">
          {#if loading}
            <Loader2 class="animate-spin" />
          {:else}
            - {formatter.format(
              (stripe_invoice.subtotal - stripe_invoice.total) / 100
            )}
          {/if}
        </div>
      </div>
      <hr class="text-gray-200 mt-2" />
      <div class="grid grid-cols-3 gap-2 mt-4">
        <div class="col-span-2 text-lg font-bold">Total</div>
        <div class="font-bold text-lg text-right">
          {#if loading}
            <Loader2 class="animate-spin" />
          {:else}
            {formatter.format(stripe_invoice.total / 100)}
          {/if}
        </div>
      </div>
    </div>
    <div class="px-12 max-w-2xl mx-auto">
      <div class="px-2 py-4 md:px-4">
        {#if addCard}
          <div transition:slide>
            <Card bind:this={card}>
              {#snippet title()}
                <div class="flex items-center justify-between">
                  <h3 class="text-xl font-bold text-tertiary mb-8">
                    Add Credit Card
                  </h3>
                  <button
                    onclick={() => (addCard = false)}
                    class="outline outline-gray-200 px-2 py-1 font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              {/snippet}
            </Card>
          </div>
        {:else}
          <div class="flex items-center">
            <h3 class="text-xl py-4 font-semibold text-tertiary flex-1">
              You will pay with:
            </h3>
            <div>
              <button
                onclick={() => (addCard = true)}
                class="outline outline-gray-200 px-2 py-1 font-semibold"
              >
                Add a new card
              </button>
            </div>
          </div>
          <ShowCard
            stripe_payment_method={signupStore.stripe_data.payment_method}
          />
        {/if}
      </div>
    </div>
    <div class="max-w-2xl mx-auto mt-4">
      <SaveButton
        bind:saving
        title="Adjust Standard Subscription"
        onclick={handleSubmit}
      />
    </div>
  </div>
</div>
